import React, { useEffect } from 'react';

const LiskovSubstitutionPrinciple = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Liskov Substitution Principle (LSP) - SOLID Principles Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Liskov Substitution Principle - objects of a superclass should be replaceable with objects of its subclasses. Learn with inheritance examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Liskov Substitution Principle - objects of a superclass should be replaceable with objects of its subclasses. Learn with inheritance examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'liskov substitution principle, LSP, SOLID principles, inheritance, polymorphism, software design, object oriented programming');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'liskov substitution principle, LSP, SOLID principles, inheritance, polymorphism, software design, object oriented programming';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Liskov Substitution Principle (LSP)</h1>
        <p style={styles.subtitle}>
          The Third SOLID Principle - Objects should be replaceable with their subtypes
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Definition</h2>
          <blockquote style={styles.blockquote}>
            "Objects of a superclass should be replaceable with objects of its subclasses without breaking the application."
          </blockquote>
          <p style={styles.paragraph}>
            The Liskov Substitution Principle ensures that inheritance is used correctly. If class B is a subclass of class A, 
            then objects of class A should be replaceable with objects of class B without altering the correctness of the program.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Key Concepts</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Behavioral Subtyping:</strong> Subtypes must be behaviorally compatible with their base types</li>
            <li style={styles.listItem}><strong>Contract Compliance:</strong> Subclasses must honor the contracts established by their parent classes</li>
            <li style={styles.listItem}><strong>Preconditions:</strong> Cannot be strengthened in subclasses</li>
            <li style={styles.listItem}><strong>Postconditions:</strong> Cannot be weakened in subclasses</li>
            <li style={styles.listItem}><strong>Invariants:</strong> Must be preserved in subclasses</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Violation Example</h2>
          <div style={styles.codeBlock}>
{`// BAD: Violates LSP - Rectangle-Square problem
class Rectangle {
    protected int width;
    protected int height;
    
    public Rectangle(int width, int height) {
        this.width = width;
        this.height = height;
    }
    
    public void setWidth(int width) {
        this.width = width;
    }
    
    public void setHeight(int height) {
        this.height = height;
    }
    
    public int getWidth() { return width; }
    public int getHeight() { return height; }
    
    public int calculateArea() {
        return width * height;
    }
}

// Problem: Square changes behavior of Rectangle
class Square extends Rectangle {
    public Square(int side) {
        super(side, side);
    }
    
    @Override
    public void setWidth(int width) {
        this.width = width;
        this.height = width; // Violates LSP - unexpected behavior
    }
    
    @Override
    public void setHeight(int height) {
        this.width = height;  // Violates LSP - unexpected behavior
        this.height = height;
    }
}

// Client code that breaks with Square
public class LSPViolationDemo {
    public static void testRectangle(Rectangle rectangle) {
        rectangle.setWidth(5);
        rectangle.setHeight(4);
        
        // Expected: 20, but Square will return 16
        int expectedArea = 5 * 4; // 20
        int actualArea = rectangle.calculateArea();
        
        assert expectedArea == actualArea : "Area calculation failed!";
    }
    
    public static void main(String[] args) {
        Rectangle rectangle = new Rectangle(3, 4);
        testRectangle(rectangle); // Works fine
        
        Square square = new Square(3);
        testRectangle(square); // Fails! Violates LSP
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Correct Implementation</h2>
          <div style={styles.codeBlock}>
{`// GOOD: Following LSP - Proper abstraction
abstract class Shape {
    public abstract int calculateArea();
    public abstract int calculatePerimeter();
}

class Rectangle extends Shape {
    protected int width;
    protected int height;
    
    public Rectangle(int width, int height) {
        this.width = width;
        this.height = height;
    }
    
    public void setWidth(int width) {
        this.width = width;
    }
    
    public void setHeight(int height) {
        this.height = height;
    }
    
    public int getWidth() { return width; }
    public int getHeight() { return height; }
    
    @Override
    public int calculateArea() {
        return width * height;
    }
    
    @Override
    public int calculatePerimeter() {
        return 2 * (width + height);
    }
}

class Square extends Shape {
    private int side;
    
    public Square(int side) {
        this.side = side;
    }
    
    public void setSide(int side) {
        this.side = side;
    }
    
    public int getSide() { return side; }
    
    @Override
    public int calculateArea() {
        return side * side;
    }
    
    @Override
    public int calculatePerimeter() {
        return 4 * side;
    }
}

// Client code works with any Shape
public class LSPCompliantDemo {
    public static void printShapeInfo(Shape shape) {
        System.out.println("Area: " + shape.calculateArea());
        System.out.println("Perimeter: " + shape.calculatePerimeter());
    }
    
    public static void main(String[] args) {
        Shape rectangle = new Rectangle(5, 4);
        Shape square = new Square(5);
        
        printShapeInfo(rectangle); // Works
        printShapeInfo(square);    // Works - LSP satisfied
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Benefits of Following LSP</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Reliable Inheritance:</strong> Subclasses can be used safely in place of parent classes</li>
            <li style={styles.listItem}><strong>Polymorphism:</strong> Enables true polymorphic behavior</li>
            <li style={styles.listItem}><strong>Code Reusability:</strong> Client code works with any subclass</li>
            <li style={styles.listItem}><strong>Maintainability:</strong> Changes to subclasses don't break client code</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Liskov Substitution Principle ensures that inheritance hierarchies are well-designed and behave predictably. 
            By following LSP, you create robust systems where subclasses can be used interchangeably with their parent classes 
            without causing unexpected behavior or breaking client code.
          </p>
          <p style={styles.paragraph}>
            Remember: inheritance should model "is-a" relationships, not "has-a" or "uses-a" relationships. 
            When in doubt, favor composition over inheritance to avoid LSP violations.
          </p>
        </section>
      </main>
    </article>
  );
};

export default LiskovSubstitutionPrinciple;